import { create } from 'zustand';
import { safeStorage } from '@/lib/storage';
import { ru, type Messages } from './ru';
import { uz } from './uz';

/**
 * To add English: create `en.ts` typed as `Messages`, add it here and to `LOCALES`.
 * The compiler then enforces that every key is translated.
 */
const dictionaries = { ru, uz } satisfies Record<string, Messages>;

export type Locale = keyof typeof dictionaries;
export const LOCALES = Object.keys(dictionaries) as Locale[];

const STORAGE_KEY = 'vybe.locale';

function initialLocale(): Locale {
  const saved = safeStorage.get('local', STORAGE_KEY);
  if (saved && saved in dictionaries) return saved as Locale;
  return typeof navigator !== 'undefined' && navigator.language.toLowerCase().startsWith('uz')
    ? 'uz'
    : 'ru';
}

interface I18nState {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

export const useLocaleStore = create<I18nState>((set) => ({
  locale: initialLocale(),
  setLocale: (locale) => {
    safeStorage.set('local', STORAGE_KEY, locale);
    document.documentElement.lang = locale;
    set({ locale });
  },
}));

export function useI18n(): { m: Messages; locale: Locale; setLocale: (l: Locale) => void } {
  const locale = useLocaleStore((s) => s.locale);
  const setLocale = useLocaleStore((s) => s.setLocale);
  return { m: dictionaries[locale], locale, setLocale };
}

export function messagesFor(locale: Locale): Messages {
  return dictionaries[locale];
}

export type { Messages };
